import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { Search, Download, Eye, Phone, MessageCircle } from "lucide-react";

import AdminLayout from "@/components/admin/AdminLayout";
import { Badge } from "./AdminDashboard";
import { listLeads, downloadCsv, SITIO_BADGE, TEMP_BADGE, STATUS_OPTIONS, FUENTE_OPTIONS } from "@/lib/admin-api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PAGE_SIZE = 25;

export default function AdminLeads() {
    const [data, setData] = useState({ total: 0, items: [] });
    const [page, setPage] = useState(0);
    const [filters, setFilters] = useState({ q: "", sitio: "", fuente: "", estatus: "", temp: "" });
    const [searchInput, setSearchInput] = useState("");

    useEffect(() => {
        const t = setTimeout(() => setFilters((f) => ({ ...f, q: searchInput })), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
        Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
        listLeads(params).then(setData).catch(() => toast.error("Error cargando leads"));
    }, [page, filters]);

    const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

    const onExport = async () => {
        try { await downloadCsv(filters); toast.success("CSV descargado"); }
        catch { toast.error("Error al exportar"); }
    };

    const setFilter = (k, v) => { setPage(0); setFilters((f) => ({ ...f, [k]: v === "all" ? "" : v })); };

    return (
        <AdminLayout title="Leads">
            <div className="bg-white border border-neutral-200 rounded-sm p-4 mb-4 flex flex-wrap gap-3 items-end" data-testid="leads-toolbar">
                <div className="flex-1 min-w-[200px]">
                    <label className="text-xs font-display font-semibold text-eclipse-muted uppercase mb-1 block">Búsqueda</label>
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-eclipse-muted" />
                        <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Nombre, email, teléfono..." className="pl-8" data-testid="leads-search" />
                    </div>
                </div>
                <SmallSelect label="Sitio" value={filters.sitio || "all"} onChange={(v) => setFilter("sitio", v)} testId="leads-filter-sitio"
                    options={[{ value: "all", label: "Todos" }, { value: "eclipse", label: "Eclipse" }, { value: "flexshade", label: "FlexShade" }, { value: "velarias", label: "Velarias" }]} />
                <SmallSelect label="Fuente" value={filters.fuente || "all"} onChange={(v) => setFilter("fuente", v)} testId="leads-filter-fuente"
                    options={[{ value: "all", label: "Todos" }, ...FUENTE_OPTIONS]} />
                <SmallSelect label="Estatus" value={filters.estatus || "all"} onChange={(v) => setFilter("estatus", v)} testId="leads-filter-estatus"
                    options={[{ value: "all", label: "Todos" }, ...STATUS_OPTIONS]} />
                <SmallSelect label="Temp" value={filters.temp || "all"} onChange={(v) => setFilter("temp", v)} testId="leads-filter-temp"
                    options={[{ value: "all", label: "Todos" }, { value: "hot", label: "HOT" }, { value: "warm", label: "WARM" }, { value: "cold", label: "COLD" }]} />
                <Button onClick={onExport} variant="outline" className="border-eclipse-cta text-eclipse-cta hover:bg-eclipse-cta hover:text-white" data-testid="leads-export-csv">
                    <Download className="w-4 h-4 mr-2" /> CSV
                </Button>
            </div>

            <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden" data-testid="leads-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-eclipse-muted">
                            <tr>
                                <th className="px-4 py-2.5 text-left">Fecha</th>
                                <th className="px-4 py-2.5 text-left">Nombre</th>
                                <th className="px-4 py-2.5 text-left">Score</th>
                                <th className="px-4 py-2.5 text-left">Sitio</th>
                                <th className="px-4 py-2.5 text-left hidden md:table-cell">Tipo</th>
                                <th className="px-4 py-2.5 text-left hidden lg:table-cell">Ciudad</th>
                                <th className="px-4 py-2.5 text-left">Estatus</th>
                                <th className="px-4 py-2.5 text-left hidden md:table-cell">Fuente</th>
                                <th className="px-4 py-2.5 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.length === 0 ? (
                                <tr><td colSpan={9} className="p-8 text-center text-eclipse-muted">Sin leads</td></tr>
                            ) : data.items.map((l) => {
                                const st = STATUS_OPTIONS.find((s) => s.value === l.estatus);
                                return (
                                    <tr key={l.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                                        <td className="px-4 py-3 text-xs text-eclipse-muted whitespace-nowrap">{l.created_at && format(parseISO(l.created_at), "d MMM HH:mm", { locale: es })}</td>
                                        <td className="px-4 py-3 font-medium">
                                            <Link to={`/admin/leads/${l.id}`} className="hover:text-eclipse-cta" data-testid={`lead-row-${l.id}`}>{l.nombre}</Link>
                                            {l.empresa && <p className="text-[11px] text-eclipse-muted">{l.empresa}</p>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-display font-black">{l.lead_score}</span>
                                                <Badge {...TEMP_BADGE[l.lead_temp]} bordered />
                                            </div>
                                        </td>
                                        <td className="px-4 py-3"><Badge {...SITIO_BADGE[l.sitio]} /></td>
                                        <td className="px-4 py-3 text-xs text-eclipse-muted hidden md:table-cell">{l.tipo_espacio || "-"}</td>
                                        <td className="px-4 py-3 text-xs text-eclipse-muted hidden lg:table-cell">{l.ciudad || "-"}</td>
                                        <td className="px-4 py-3"><Badge label={st?.label || l.estatus} color={st?.color} bordered /></td>
                                        <td className="px-4 py-3 text-xs text-eclipse-muted capitalize hidden md:table-cell">{l.fuente}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1.5">
                                                <Link to={`/admin/leads/${l.id}`} className="p-1.5 hover:text-eclipse-cta" title="Ver detalle" data-testid={`lead-row-view-${l.id}`}><Eye className="w-4 h-4" /></Link>
                                                {l.telefono && <a href={`tel:${l.telefono.replace(/\D/g, "")}`} className="p-1.5 hover:text-eclipse-cta" title="Llamar"><Phone className="w-4 h-4" /></a>}
                                                {l.telefono && <a href={`https://wa.me/${l.telefono.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:text-green-600" title="WhatsApp"><MessageCircle className="w-4 h-4" /></a>}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-200 text-sm">
                    <p className="text-eclipse-muted">{data.total} resultado{data.total !== 1 ? "s" : ""}</p>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="leads-prev-page">Anterior</Button>
                        <span className="text-xs text-eclipse-muted">{page + 1} / {totalPages}</span>
                        <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} data-testid="leads-next-page">Siguiente</Button>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}

function SmallSelect({ label, value, onChange, options, testId }) {
    return (
        <div>
            <label className="text-xs font-display font-semibold text-eclipse-muted uppercase mb-1 block">{label}</label>
            <Select value={value} onValueChange={onChange}>
                <SelectTrigger className="w-36" data-testid={testId}><SelectValue /></SelectTrigger>
                <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
        </div>
    );
}
